import { useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';
import { Stack } from '@/ui/line/line';

import { SectionDivider, StatusDot } from './mcp-oauth.styles';
import type { AccountScopedMcpConnectorConfig } from './mcp-oauth-connector-config';
import { McpOAuthSetupSteps, type SetupStep } from './mcp-oauth-setup-steps';

type SetupTab = 'claudeAiDesktop' | 'code';

const CLAUDE_AI_DESKTOP_NAME_STEP_INDEX = 2;

const SETUP_TABS: SetupTab[] = ['claudeAiDesktop', 'code'];

const SETUP_TAB_LABEL_KEYS: Record<
  SetupTab,
  'mcpOAuth.tabClaudeAiDesktop' | 'mcpOAuth.tabCode'
> = {
  claudeAiDesktop: 'mcpOAuth.tabClaudeAiDesktop',
  code: 'mcpOAuth.tabCode',
};

const SETUP_TAB_STEPS_KEYS: Record<
  SetupTab,
  'mcpOAuth.stepsClaudeAiDesktop' | 'mcpOAuth.stepsClaudeCode'
> = {
  claudeAiDesktop: 'mcpOAuth.stepsClaudeAiDesktop',
  code: 'mcpOAuth.stepsClaudeCode',
};

const withStagingAdvancedSettingsHint = (
  steps: SetupStep[],
  showAdvancedSettingsHint: boolean,
): SetupStep[] => {
  if (!showAdvancedSettingsHint) {
    return steps;
  }

  return steps.map((step, index) =>
    index === CLAUDE_AI_DESKTOP_NAME_STEP_INDEX
      ? {
          ...step,
          hintI18nKey: 'mcpOAuth.stepsClaudeAiDesktopAdvancedSettingsHint',
        }
      : step,
  );
};

interface McpOAuthSetupTabsProps {
  connectorConfig: AccountScopedMcpConnectorConfig;
  copiedFieldId: string | null;
  onCopy: (value: string, fieldId: string) => void;
  showAdvancedSettingsHint: boolean;
  showStatusFooter?: boolean;
  showUrlField?: boolean;
}

export const McpOAuthSetupTabs = ({
  connectorConfig,
  copiedFieldId,
  onCopy,
  showAdvancedSettingsHint,
  showStatusFooter = false,
  showUrlField = true,
}: McpOAuthSetupTabsProps) => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });
  const [selectedTab, setSelectedTab] = useState<SetupTab>('claudeAiDesktop');

  const activeSteps = useMemo(() => {
    const steps = t(SETUP_TAB_STEPS_KEYS[selectedTab], {
      returnObjects: true,
    }) as SetupStep[];

    if (selectedTab !== 'claudeAiDesktop') {
      return steps;
    }

    return withStagingAdvancedSettingsHint(steps, showAdvancedSettingsHint);
  }, [selectedTab, showAdvancedSettingsHint, t]);

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    const currentIndex = SETUP_TABS.indexOf(selectedTab);
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    const nextIndex =
      (currentIndex + direction + SETUP_TABS.length) % SETUP_TABS.length;

    setSelectedTab(SETUP_TABS[nextIndex]);
  };

  const activeTabId = `mcp-oauth-tab-${selectedTab}`;
  const activePanelId = `mcp-oauth-panel-${selectedTab}`;

  return (
    <Stack fullWidth gap="16px">
      {showUrlField ? (
        <UrlField alignItems="center" fullWidth gap="8px">
          <UrlTextContainer>
            <UrlText title={connectorConfig.mcpUrl}>
              {connectorConfig.mcpUrl}
            </UrlText>
          </UrlTextContainer>
          <UrlIconCopyButton
            aria-label={t`mcpOAuth.copyUrl`}
            onClick={() => {
              void onCopy(connectorConfig.mcpUrl, 'mcp-url');
            }}
            type="button"
          >
            <Icon color="currentColor" height="15px" icon="Copy" width="15px" />
          </UrlIconCopyButton>
        </UrlField>
      ) : null}

      <Stack fullWidth gap="12px">
        <TabRow
          aria-label={t`mcpOAuth.setupTitle`}
          fullWidth
          gap="22px"
          role="tablist"
        >
          {SETUP_TABS.map(tab => {
            const tabId = `mcp-oauth-tab-${tab}`;

            return (
              <TabButton
                $isActive={selectedTab === tab}
                aria-controls={`mcp-oauth-panel-${tab}`}
                aria-selected={selectedTab === tab}
                id={tabId}
                key={tab}
                onClick={() => {
                  setSelectedTab(tab);
                }}
                onKeyDown={handleTabKeyDown}
                role="tab"
                type="button"
              >
                {t(SETUP_TAB_LABEL_KEYS[tab])}
              </TabButton>
            );
          })}
        </TabRow>

        <TabPanel
          aria-labelledby={activeTabId}
          fullWidth
          id={activePanelId}
          role="tabpanel"
        >
          <McpOAuthSetupSteps
            claudeCodeCommand={connectorConfig.claudeCodeCommand}
            connectorName={connectorConfig.connectorName}
            copiedFieldId={copiedFieldId}
            copyCommandLabel={t`mcpOAuth.copy`}
            copyCopiedLabel={t`mcpOAuth.copied`}
            copyNameLabel={t`mcpOAuth.copyName`}
            mcpUrl={connectorConfig.mcpUrl}
            onCopy={onCopy}
            steps={activeSteps}
            stepsKey={SETUP_TAB_STEPS_KEYS[selectedTab]}
          />
        </TabPanel>
      </Stack>

      {showStatusFooter ? (
        <>
          <SectionDivider />
          <StatusFooter alignItems="center" fullWidth gap="8px">
            <StatusDot $status="notConnected" />
            <StatusFooterText>{t`mcpOAuth.statusNotConnected`}</StatusFooterText>
          </StatusFooter>
        </>
      ) : null}
    </Stack>
  );
};

const UrlField = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.spotlight.surface.default};
  border-radius: 8px;
  min-width: 0;
  overflow: hidden;
  padding: 10px 10px 10px 12px;
`;

const UrlTextContainer = styled('div')`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
`;

const UrlText = styled('code')`
  color: ${({ theme }) => theme.colors.gray.c12};
  display: block;
  font-family: ui-monospace, monospace;
  font-size: 12.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const UrlIconCopyButton = styled('button')`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gray.c10};
  cursor: pointer;
  flex-shrink: 0;
  line-height: 0;
  padding: 2px;
`;

const TabRow = styled(Flexbox)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray.c4};
  flex-wrap: wrap;
`;

const TabPanel = styled(Flexbox)``;

const TabButton = styled('button')<{ $isActive: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ $isActive, theme }) =>
      $isActive ? theme.colors.blue.primaryA : 'transparent'};
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.basics.black : theme.colors.gray.c10};
  cursor: pointer;
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13.5px;
  font-weight: ${({ $isActive }) => ($isActive ? 500 : 400)};
  line-height: 1.4;
  margin-bottom: -1px;
  padding: 0 0 9px;
  white-space: nowrap;
`;

const StatusFooter = styled(Flexbox)``;

const StatusFooterText = styled('span')`
  color: ${({ theme }) => theme.colors.gray.c10};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
`;
